import { useState, useEffect, useCallback } from 'react';
import { X, ExternalLink, Link2, Lock } from 'lucide-react';
import { useT } from '../i18n';
import { api } from '../api/client';
import { DateHistory, formatPlannedSlot } from './DateHistory';
import { TaskTodoList } from '../proposals/vue-jalons-taches/TaskTodoList';
import { DocumentsList } from '../proposals/documents/DocumentsList';
import { ContactTaskCreator } from './ContactTaskCreator';
import { RemindTaskAssignee } from './RemindTaskAssignee';
import type { TaskDetail, TaskUpdate } from '../types';
import { taskStatusName } from '../utils/taskStatusName';

interface TaskDetailPanelProps {
  taskId: number;
  currentUserId: number;
  /** The board row (project) this panel was opened from — lets the "linked
   *  project" indicator show whichever of the task's two projects isn't this one. */
  contextProjectId?: number | null;
  onClose: () => void;
  onOpenDetail: () => void;
  onDeleted: () => void;
  onUpdated: () => void;
}

export function TaskDetailPanel({ taskId, currentUserId, contextProjectId, onClose, onOpenDetail, onDeleted, onUpdated }: TaskDetailPanelProps) {
  const { t } = useT();
  const [task, setTask] = useState<TaskDetail | null>(null);
  const [loading, setLoading] = useState(true);

  const loadTask = useCallback(async () => {
    try {
      const data = await api.getTaskDetail(taskId) as TaskDetail;
      setTask(data);
    } catch {
      // task not found
    } finally {
      setLoading(false);
    }
  }, [taskId]);

  useEffect(() => { loadTask(); }, [loadTask]);

  const formatTime = (dateStr: string) => {
    const d = new Date(dateStr);
    return d.toLocaleDateString(undefined, { day: 'numeric', month: 'short' }) + ' ' +
      d.toLocaleTimeString(undefined, { hour: '2-digit', minute: '2-digit' });
  };

  if (loading) {
    return (
      <div className="panel-overlay open" onClick={onClose}>
        <div className="panel-overlay-bg" />
        <div className="panel-slide" onClick={(e) => e.stopPropagation()}>
          <div className="panel-slide-body" style={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            {t('common.loading')}
          </div>
        </div>
      </div>
    );
  }

  if (!task) return null;

  // The top badge shows whichever project this panel was opened from
  // (falling back to the primary project without board context); the
  // "linked to" indicator shows every other one of the task's projects. Both
  // derive from the same list so the current project can never also show up
  // in "linked to" (and vice versa).
  const allTaskProjects = [
    { id: task.project_id, name: task.project_name, code: task.project_code as string | null },
    ...task.linked_projects.map((lp) => ({ id: lp.id, name: lp.name, code: lp.project_code })),
  ];
  const effectiveContext = contextProjectId ?? task.project_id;
  const currentProject = allTaskProjects.find((p) => p.id === effectiveContext) ?? allTaskProjects[0];
  const otherProjects = allTaskProjects.filter((p) => p.id !== currentProject.id);
  // Editable only from the home project — see TaskDetailModal/TaskDetailView.
  const isSource = currentProject.id === task.project_id;

  return (
    <div className="panel-overlay open" onClick={onClose}>
      <div className="panel-overlay-bg" />
      <div className="panel-slide" onClick={(e) => e.stopPropagation()}>
        <div className="panel-slide-head">
          <div className="panel-slide-title">{task.title}</div>
          <div style={{ display: 'flex', gap: 'var(--s8)', alignItems: 'center', flexShrink: 0 }}>
            <button
              className="btn btn-ghost btn-sm"
              onClick={onOpenDetail}
              title={t('taskDetail.openFullDetail')}
            >
              <ExternalLink size={14} />
            </button>
            <button className="panel-slide-close" onClick={onClose}><X size={16} /></button>
          </div>
        </div>

        <div className="panel-slide-body">
          {/* Badges */}
          <div className="detail-badges">
            <span className="badge badge-accent">{currentProject.name}</span>
            {currentProject.code && <span className="badge">{currentProject.code.toUpperCase()}</span>}
            <span className="badge">{taskStatusName(task.column_label, t)}</span>
            {!isSource && (
              <span className="badge" title={t('tasks.readOnlyNotice')}>
                <Link2 size={11} style={{ marginRight: 4, verticalAlign: -1 }} />
                {t('tasks.primaryProjectPrefix')} {task.project_code?.toUpperCase() || task.project_name}
              </span>
            )}
            {isSource && otherProjects.length > 0 && (
              <span className="badge" title={t('tasks.linkedProject')}>
                <Link2 size={11} style={{ marginRight: 4, verticalAlign: -1 }} />
                {t('tasks.presentInProjects', { projects: otherProjects.map((p) => p.code?.toUpperCase() || p.name).join(', ') })}
              </span>
            )}
          </div>

          {/* Description */}
          {task.description && (
            <div className="detail-section">
              <div className="detail-label">{t('common.description')}</div>
              <div className="detail-text">{task.description}</div>
            </div>
          )}

          {/* [Proposition: vue-jalons-taches] To-do list */}
          <div className="detail-section">
            <TaskTodoList taskId={task.id} readOnly={!isSource} showHeader onChanged={() => { loadTask(); onUpdated(); }} taskDueDate={task.due_date} />
          </div>
          {/* [Proposition: documents] */}
          <div className="detail-section">
            <DocumentsList target={{ type: 'task', id: task.id }} readOnly={!isSource} />
          </div>

          {/* Metadata */}
          <div className="detail-section">
            <div className="detail-label">{t('common.details')}</div>
            <div className="detail-row">
              <span className="detail-row-label">{t('taskDetail.metaDueDate')}</span>
              {task.due_date
                ? new Date(task.due_date).toLocaleDateString(undefined, { day: 'numeric', month: 'short', year: 'numeric' })
                : <span style={{ color: 'var(--ink-3)' }}>{t('taskDetail.notSet')}</span>
              }
            </div>
            <DateHistory history={task.due_date_history} label={t('taskDetail.metaDueDate')} />
            {task.planned_at && (
              <div className="detail-row">
                <span className="detail-row-label">{t('taskDetail.metaPlannedDate')}</span>
                {new Date(task.planned_at).toLocaleDateString(undefined, { day: 'numeric', month: 'short', year: 'numeric' })}
                {' '}
                {new Date(task.planned_at).toLocaleTimeString(undefined, { hour: '2-digit', minute: '2-digit' })}
              </div>
            )}
            <DateHistory history={task.planned_at_history} label={t('taskDetail.metaPlannedDate')} formatValue={formatPlannedSlot} />
            <div className="detail-row">
              <span className="detail-row-label">{t('taskDetail.assignee')}</span>
              {task.assignee_name || <span style={{ color: 'var(--ink-3)' }}>{t('taskDetail.unassigned')}</span>}
            </div>
            {/* [Proposition: collaborateurs-tache] */}
            <div className="detail-row">
              <span className="detail-row-label">{t('collab.label')}</span>
              {task.collaborator_names?.length ? task.collaborator_names.join(', ') : <span style={{ color: 'var(--ink-3)' }}>{t('collab.none')}</span>}
            </div>
            <div className="detail-row">
              <span className="detail-row-label">{t('tasks.privacyLabel')}</span>
              {task.is_private
                ? <span style={{ color: 'var(--warn)', display: 'inline-flex', alignItems: 'center', gap: 4 }}><Lock size={12} /> {t('tasks.privateTaskShort')}</span>
                : t('tasks.publicTask')}
            </div>
            {task.created_by_name && (
              <div className="detail-row">
                <span className="detail-row-label">{t('taskDetail.createdBy')}</span>
                {task.created_by_name}
              </div>
            )}
            <div style={{ marginTop: 'var(--s8)' }}>
              <ContactTaskCreator taskId={task.id} creatorId={task.created_by} creatorName={task.created_by_name} currentUserId={currentUserId} onSent={loadTask} />
              <RemindTaskAssignee taskId={task.id} canRemind={task.can_remind} assigneeName={task.assignee_name} onSent={loadTask} />
            </div>
          </div>

          {/* Dependencies */}
          {(task.depends_on.length > 0 || task.enables.length > 0) && (
            <div className="detail-section">
              <div className="detail-label">{t('taskDetail.dependencies')}</div>
              {task.depends_on.length > 0 && (
                <>
                  <div style={{ fontSize: 'var(--t-11)', color: 'var(--ink-3)', marginBottom: 'var(--s4)' }}>{t('taskDetail.dependentOn')}</div>
                  <div className="task-dep-list" style={{ marginBottom: 'var(--s8)' }}>
                    {task.depends_on.map((d) => (
                      <div key={d.dep_id} className="task-dep-item">
                        <span className="task-dep-item-title">{d.title}</span>
                        <span className="task-dep-item-status">{taskStatusName(d.column_label, t)}</span>
                      </div>
                    ))}
                  </div>
                </>
              )}
              {task.enables.length > 0 && (
                <>
                  <div style={{ fontSize: 'var(--t-11)', color: 'var(--ink-3)', marginBottom: 'var(--s4)' }}>{t('taskDetail.enables')}</div>
                  <div className="task-dep-list">
                    {task.enables.map((d) => (
                      <div key={d.dep_id} className="task-dep-item">
                        <span className="task-dep-item-title">{d.title}</span>
                        <span className="task-dep-item-status">{taskStatusName(d.column_label, t)}</span>
                      </div>
                    ))}
                  </div>
                </>
              )}
            </div>
          )}

          {/* Updates / comments (read-only) */}
          {task.updates.length > 0 && (
            <div className="detail-section">
              <div className="detail-label">{t('taskDetail.activity')}</div>
              <div className="task-updates-list">
                {task.updates.map((u: TaskUpdate) => (
                  <div
                    key={u.id}
                    className={`task-update ${u.update_type === 'question' ? 'task-update--question' : ''} ${u.update_type === 'reply' ? 'task-update--reply' : ''}`}
                  >
                    <div className="task-update-header">
                      <span className="task-update-author">{u.user_name}</span>
                      <span className="task-update-time">{formatTime(u.created_at)}</span>
                      {u.update_type !== 'comment' && (
                        <span className="task-update-type">{u.update_type}</span>
                      )}
                    </div>
                    <div className="task-update-content">{u.content}</div>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
